import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { apiGet, apiPost, apiDelete, guestLogin as apiGuestLogin, markOnboarded as apiMarkOnboarded, updateHistoryFilter as apiUpdateHistoryFilter, getPurchaseSummary, listWaits, clearToken, saveLocation, clearLocation } from "@/lib/api";
import { tierFor } from "@/lib/tiers";
import { TEST_MODE } from "@/lib/config";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// In Test Mode, force the developer into VIP Gold with a huge balance.
// Guests are excluded so their real 1-credit trial limit is enforced.
// `real_plan` always preserves the true subscription for strict feature gating (e.g. Analyze paywall).
const applyTestMode = (u) => {
  if (!u) return u;
  if (TEST_MODE && !u.is_guest) return { ...u, real_plan: u.plan, plan: "gold", credits: 9999 };
  return { ...u, real_plan: u.plan };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [purchaseSummary, setPurchaseSummary] = useState({ cash_saved: 0, real_cash_saved: 0, tax_rate: 0.08, location_known: false, location_label: null, purchases_count: 0, items: [] });
  const [waits, setWaits] = useState([]);
  const [replay, setReplay] = useState(null);
  const [launch, setLaunch] = useState(null); // Home → jump to a tab + preselect a mode/query
  const [guestExhausted, setGuestExhausted] = useState(false);
  // Tracks whether a real session was ever established, so a benign logged-out 401
  // (initial /auth/me) never triggers a redirect — only a session that DROPS mid-use.
  const wasAuthed = useRef(false);
  useEffect(() => { if (user) wasAuthed.current = true; }, [user]);

  // Any 402 from the API (search or charge) means the guest trial is spent.
  useEffect(() => {
    const onExhausted = () => setGuestExhausted(true);
    window.addEventListener("guest-exhausted", onExhausted);
    return () => window.removeEventListener("guest-exhausted", onExhausted);
  }, []);

  // Session dropped (401) while the user was active → wipe client state and force a
  // clean redirect to the secure login shell (never leave a broken permission-blocked dashboard).
  useEffect(() => {
    const onExpired = () => {
      if (!wasAuthed.current) return;
      wasAuthed.current = false;
      // Gracefully invalidate the local session. We DON'T hard-reload the page
      // (window.location.assign) — that was the source of the reopen "open/close"
      // refresh loop in Edge: a full reload re-hit /app, briefly re-authed, then
      // re-expired, reloading again. Clearing user state lets React Router's
      // ProtectedRoute redirect /app → / cleanly in-app (no reload, no loop).
      clearToken();
      setUser(null);
      setHistory([]);
      setHistoryLoaded(false);
      try { sessionStorage.setItem("cdd_session_expired", "1"); } catch { /* storage unavailable */ }
    };
    window.addEventListener("session-expired", onExpired);
    return () => window.removeEventListener("session-expired", onExpired);
  }, []);

  const guestLogin = useCallback(async () => {
    const me = await apiGuestLogin();
    setUser(applyTestMode(me));
    return me;
  }, []);

  const refreshHistory = useCallback(async () => {
    try {
      const data = await apiGet("/credits/history");
      setHistory(data.history || []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoaded(true);
    }
  }, []);

  // Confirmed-purchase savings — drives the Home dashboard "Cash Saved" (never search-derived).
  const refreshPurchases = useCallback(async () => {
    try {
      const data = await getPurchaseSummary();
      setPurchaseSummary({
        cash_saved: data?.cash_saved || 0,
        real_cash_saved: data?.real_cash_saved || 0,
        tax_rate: typeof data?.tax_rate === "number" ? data.tax_rate : 0.08,
        location_known: !!data?.location_known,
        location_label: data?.location_label || null,
        purchases_count: data?.purchases_count || 0,
        items: Array.isArray(data?.items) ? data.items : [],
      });
    } catch {
      setPurchaseSummary({ cash_saved: 0, real_cash_saved: 0, tax_rate: 0.08, location_known: false, location_label: null, purchases_count: 0, items: [] });
    }
  }, []);

  // Wait list — "save and check later" watchlist shown in the dashboard.
  const refreshWaits = useCallback(async () => {
    try {
      const data = await listWaits();
      setWaits(Array.isArray(data?.waits) ? data.waits : []);
    } catch {
      setWaits([]);
    }
  }, []);

  // Persist the onboarding-completed flag to the cloud account (survives reinstall).
  const markOnboarded = useCallback(async () => {
    setUser((u) => (u ? { ...u, onboarding_completed: true } : u));
    try { await apiMarkOnboarded(); } catch (e) { console.error("markOnboarded failed (optimistic, ignored):", e); }
  }, []);

  // Save the user's US state → local tax rate, then refresh Real Money savings.
  const saveUserLocation = useCallback(async ({ stateCode = "", zip = "", lat = null, lng = null, source = "manual" }) => {
    const data = await saveLocation({ stateCode, zip, lat, lng, source });
    setUser((u) => (u ? { ...u, location: data.location } : u));
    refreshPurchases();
    return data;
  }, [refreshPurchases]);

  // Clear the stored state (privacy toggle off).
  const clearUserLocation = useCallback(async () => {
    await clearLocation();
    setUser((u) => (u ? { ...u, location: null } : u));
    refreshPurchases();
  }, [refreshPurchases]);

  const checkAuth = useCallback(async () => {
    try {
      const me = await apiGet("/auth/me");
      setUser(applyTestMode(me));
      refreshHistory();
      refreshPurchases();
      refreshWaits();
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [refreshHistory, refreshPurchases, refreshWaits]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Deduct credits for a successful (non-replay) search execution.
  // Caches the full result snapshot on the server transaction so the Saved/History
  // view MIRRORS this credit log on every device and can be replayed for FREE.
  const chargeCredits = useCallback(async (mode, query, results = null, kind = null, opts = {}) => {
    const { tier, cost, tab } = tierFor(mode);
    try {
      const trimmed = Array.isArray(results) ? results.slice(0, 40) : results;
      const data = await apiPost("/credits/charge", {
        query, mode, tab, tier, cost, label: query, kind, results: trimmed,
        budget: opts.budget ?? null, budget_max: opts.budgetMax ?? null,
      });
      // Keep the balance pinned at 9999 in Test Mode (non-guests); else reflect the server.
      setUser((u) => (u ? { ...u, credits: TEST_MODE && !u.is_guest ? 9999 : data.credits } : u));
      setHistory((h) => [data.transaction, ...h]);
      return data;
    } catch (e) {
      if (e.status === 402) setGuestExhausted(true); // guest trial used up → prompt sign-in
      throw e;
    }
  }, []);

  // Persist the applied max-budget filter on a saved search so replays keep the narrowed view.
  const updateHistoryFilter = useCallback(async (txId, budget) => {
    if (!txId) return;
    setHistory((h) => h.map((t) => (t.tx_id === txId ? { ...t, budget } : t)));
    try { await apiUpdateHistoryFilter(txId, budget); } catch (e) { console.error("updateHistoryFilter failed (optimistic, ignored):", e); }
  }, []);

  // Delete one saved entry (removes from both the credit log and Saved/History).
  const deleteHistory = useCallback(async (txId) => {
    setHistory((h) => h.filter((t) => t.tx_id !== txId));
    try { await apiDelete(`/credits/history/${txId}`); } catch (e) { console.error("deleteHistory failed (optimistic, ignored):", e); }
  }, []);

  // Clear all saved entries for a given tab.
  const clearHistoryTab = useCallback(async (tab) => {
    setHistory((h) => h.filter((t) => t.tab !== tab));
    try { await apiDelete(`/credits/history?tab=${encodeURIComponent(tab)}`); } catch (e) { console.error("clearHistoryTab failed (optimistic, ignored):", e); }
  }, []);

  const logout = async () => {
    try {
      await apiPost("/auth/logout");
    } catch (e) {
      console.error("logout request failed (clearing client state anyway):", e);
    }
    clearToken();
    setUser(null);
    setHistory([]);
    setHistoryLoaded(false);
  };

  const clearReplay = useCallback(() => setReplay(null), []);
  const clearLaunch = useCallback(() => setLaunch(null), []);

  return (
    <AuthContext.Provider
      value={{
        user, setUser, loading, checkAuth,
        history, historyLoaded, refreshHistory, chargeCredits, deleteHistory, clearHistoryTab, updateHistoryFilter,
        purchaseSummary, refreshPurchases,
        waits, refreshWaits,
        replay, setReplay, clearReplay, launch, setLaunch, clearLaunch, logout, markOnboarded, saveUserLocation, clearUserLocation,
        guestLogin, guestExhausted, setGuestExhausted,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
